import React, { useEffect, useState } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { Formik } from "formik";
import { showToast } from "../utils/toastUtils";
import { FaSpinner } from "react-icons/fa";
import InputComponent from "./InputComponent";
import { doctorValidation } from "../utils/validations/doctorValidation";
import { sendOtp } from "../services/authApi";
import { serviceAddDoctorSubmit, serviceUpdateDoctor } from "../services/api";
import * as Yup from "yup";
import { jwtDecode } from "jwt-decode";
import TimeSelect from "../../commonComponent/timePicker";
import moment from "moment";

const AddDoctorModal = ({
  isOpen,
  onClose,
  fetchDoctorList,
  isEdit = false,
  doctor,
}) => {
  const [showVerifyUI, setShowVerifyUI] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");
  const [timePickerOpen, setTimePickerOpen] = useState({});

  const [otp, setOtp] = useState("");

  const defaultWorkingDays = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  };

  const defaultWorkingHours = {
    monday: [
      { start: null, end: null, enabled: false },
      { start: null, end: null, enabled: false },
    ],
    tuesday: [
      { start: null, end: null, enabled: false },
      { start: null, end: null, enabled: false },
    ],
    wednesday: [
      { start: null, end: null, enabled: false },
      { start: null, end: null, enabled: false },
    ],
    thursday: [
      { start: null, end: null, enabled: false },
      { start: null, end: null, enabled: false },
    ],
    friday: [
      { start: null, end: null, enabled: false },
      { start: null, end: null, enabled: false },
    ],
    saturday: [
      { start: null, end: null, enabled: false },
      { start: null, end: null, enabled: false },
    ],
    sunday: [
      { start: null, end: null, enabled: false },
      { start: null, end: null, enabled: false },
    ],
  };

  const [workingDays, setWorkingDays] = useState(defaultWorkingDays);
  const [workingHours, setWorkingHours] = useState(defaultWorkingHours);
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    if (isEdit && doctor) {
      const workingDaysFromApi = doctor?.availibility?.days;
      const workingDays = { ...defaultWorkingDays };

      if (workingDaysFromApi) {
        Object.keys(workingDaysFromApi).forEach((day) => {
          if (workingDaysFromApi[day]) {
            workingDays[day] = true;
          }
        });
      }

      const workingHoursFromApi = doctor?.availibility?.hours;
      const workingHours = { ...defaultWorkingHours };

      if (workingHoursFromApi) {
        Object.keys(workingHoursFromApi)
          .sort()
          .forEach((day) => {
            if (Array.isArray(workingHoursFromApi[day])) {
              workingHours[day] = workingHoursFromApi[day].map((slot) => ({
                start: slot.start || "",
                end: slot.end || "",
                enabled: slot.enabled || false,
              }));
            } else {
              workingHours[day] = [
                {
                  start: "",
                  end: "",
                  enabled: false,
                },
                {
                  start: "",
                  end: "",
                  enabled: false,
                },
              ];
            }
          });
      }

      setWorkingDays(workingDays);
      setWorkingHours(workingHours);
    }
  }, [isEdit, doctor]);

  //const [doctors, setDoctors] = useState([]);
  const handleClear = () => {
    setWorkingDays(defaultWorkingDays);
    setWorkingHours(defaultWorkingHours);
  };

  const handleWorkingHoursChange = (day, index, field, value) => {
    const updatedHours = [...workingHours[day]];
    updatedHours[index] = { ...updatedHours[index], [field]: value };

    setWorkingHours({
      ...workingHours,
      [day]: updatedHours,
    });
  };

  const handleLocalSubmit = async (values) => {
    setIsSubmitting(true); // Start submitting, disable the button
    try {
      if (showVerifyUI) {
        if (otpSent) {
          // Pass the data up to the parent onSubmit handler
          await handleAddDoctorSubmit({ ...values, otp });
        }
      } else {
        // If verification UI is not shown, submit the form without OTP
        await handleAddDoctorSubmit({ ...values, otp: "" });
      }
      setIsSubmitting(false); // Submission finished, enable the button.
    } catch (error) {
      console.error("Error submitting form:", error);
      setError("Failed to submit the form. Please try again.");
      setIsSubmitting(false); // Error occurred, enable the button
    }
  };

  const validateWorkingHours = (day) => {
    const dayHours = workingHours[day];

    if (dayHours[0].enabled && dayHours[1].enabled) {
      if (
        dayHours[0].start &&
        dayHours[0].end &&
        dayHours[0].end.isBefore(dayHours[0].start)
      ) {
        showToast("Morning end time should be after start time", "error");
        return false;
      }

      if (
        dayHours[1].start &&
        dayHours[1].end &&
        dayHours[1].end.isBefore(dayHours[1].start)
      ) {
        showToast("Evening end time should be after start time", "error");
        return false;
      }

      if (
        dayHours[0].end &&
        dayHours[1].start &&
        dayHours[0].end.isAfter(dayHours[1].start)
      ) {
        showToast(
          "Morning and evening time intervals should not overlap",
          "error"
        );
        return false;
      }
    }

    return true;
  };

  const copyTimingsToOtherDays = (sourceDay) => {
    const sourceTimings = workingHours[sourceDay];
    const updatedHours = { ...workingHours };
    Object.keys(updatedHours).forEach((day) => {
      if (workingDays[day]) {
        updatedHours[day] = [...sourceTimings];
      }
    });
    setWorkingHours(updatedHours);
  };

  const validatePhoneNumber = async (phoneNumber) => {
    try {
      if (!phoneNumber) {
        setError("Phone number is required.");
        return false;
      }
      doctorValidation.fields.phoneNumber.validateSync(phoneNumber);
      return true;
    } catch (error) {
      return false;
    }
  };

  const sendOtpToServer = async (phoneNumber) => {
    const response = await sendOtp(phoneNumber);
    if (response?.status) {
      showToast("OTP sent successfully", "success");
      return true;
    } else {
      setError("Failed to send OTP. Please try again.");
      return false;
    }
  };

  const handleModalClose = () => {
    handleClear();
    onClose();
    setOtp("");
    setOtpSent(false);
    setShowVerifyUI(false);
    setError("");
  };

  const handleAddDoctorSubmit = async (values) => {
    const wdays = Object.entries(workingDays)
      .filter(([_, value]) => value)
      .map(([key, _]) => key);
    const dayBasedWorkingHours = wdays.reduce((acc, day) => {
      acc[day] = workingHours[day]
        ? workingHours[day].map((slot) =>
            slot.enabled ? slot : { ...slot, start: "", end: "" }
          )
        : [];
      return acc;
    }, {});

    const token = localStorage.getItem("token");
    const decodedToken = jwtDecode(token);
    const doctorData = {
      userId: decodedToken.user_id,
      first_name: values.firstName,
      last_name: values.lastName,
      phone_number: values.phoneNumber,
      email: values.email,
      experience: values.experience,
      speciality: values.speciality,
      qualification: values.qualification,
      working_days: workingDays,
      working_hours: dayBasedWorkingHours,
      consultationFee: values.consultationFee,
    };

    if (isEdit) {
      doctorData.otp = values.otp;
    }

    try {
      const response = isEdit
        ? await serviceUpdateDoctor(doctor?.doctor_id, doctorData)
        : await serviceAddDoctorSubmit(doctorData);
      if (response?.message === "Doctor updated successfully.") {
        showToast("Doctor added successfully.", "success");
        fetchDoctorList();
        handleModalClose();
      } else {
        showToast(response.message, "error");
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        // Handle the validation error
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
      } else {
        console.error("Network error:", error);
        showToast("Network error. Please try again.", "error");
      }
    }
  };

  const isFirstDayTimingsFilled = () => {
    const firstDay = Object.keys(workingDays).find((day) => workingDays[day]);
    if (!firstDay) return false;

    const dayTimings = workingHours[firstDay];
    return (
      dayTimings[0].enabled &&
      dayTimings[0].start &&
      dayTimings[0].end &&
      dayTimings[1].enabled &&
      dayTimings[1].start &&
      dayTimings[1].end
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-start pt-10">
      <div className="relative bg-white rounded-lg shadow-xl mx-4 md:mx-0 w-full max-w-2xl p-5 px-10">
        <div className="text-center">
          <div className="mb-5 flex justify-between border-b pb-3">
            <span className="text-xl font-semibold text-gray-800 mt-3">
              {isEdit ? "Edit Doctor" : "Add Doctor"}
            </span>

            <button
              onClick={handleModalClose}
              className="absolute right-3 top-3 text-gray-600 bg-gray-100 hover:bg-gray-300 focus:ring-4 focus:outline-none focus:ring-gray-200 hover:text-gray-800 px-0 w-10 h-10 inline-flex justify-center items-center rounded-full text-2xl m-1.5"
            >
              <IoCloseOutline />
            </button>
          </div>

          <Formik
            initialValues={{
              firstName: isEdit ? doctor?.first_name : "",
              lastName: isEdit ? doctor?.last_name : "",
              email: isEdit ? doctor?.primary_email_id : "",
              phoneNumber: isEdit ? doctor?.primary_phone_number : "",
              experience: isEdit ? doctor?.experience : "",
              speciality: isEdit ? doctor?.doctor_speciality : "",
              qualification: isEdit ? doctor?.doctor_qualification : "",
              consultationFee: isEdit ? doctor?.consultation_fees : "",
            }}
            validationSchema={doctorValidation}
            validateOnChange={false}
            onSubmit={(values, { setSubmitting }) => {
              handleLocalSubmit(values);

              setSubmitting(false);
            }}
          >
            {({ values, handleChange, handleSubmit, resetForm, errors }) => (
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-[1fr_1fr] gap-y-5 gap-x-5">
                  <div>
                    <InputComponent
                      label="First Name"
                      name="firstName"
                      type="text"
                      placeholder="First Name"
                      value={values.firstName}
                      onChange={handleChange}
                      className=" w-full"
                      error={errors?.firstName} // Pass error prop here
                    />
                  </div>
                  <div>
                    <InputComponent
                      label="Last Name"
                      name="lastName"
                      type="text"
                      placeholder="Last Name"
                      value={values.lastName}
                      onChange={handleChange}
                      className=" w-full"
                      error={errors?.lastName} // Pass error prop here
                    />
                  </div>
                  <div>
                    <InputComponent
                      label="Email"
                      name="email"
                      type="email"
                      placeholder="Email"
                      value={values.email}
                      onChange={handleChange}
                      className=" w-full"
                      error={errors?.email} // Pass error prop here
                    />
                  </div>
                  <div>
                    <InputComponent
                      label="Phone Number"
                      name="phoneNumber"
                      type="tel"
                      placeholder="Phone Number"
                      value={values.phoneNumber}
                      onChange={handleChange}
                      className=" w-full"
                      error={errors?.phoneNumber} // Pass error prop here
                    />
                  </div>
                  <div>
                    <InputComponent
                      label="Total Year Experience"
                      name="experience"
                      type="tel"
                      placeholder="Total Experience (Years)"
                      value={values.experience}
                      onChange={handleChange}
                      className=" w-full"
                      error={errors?.experience} // Pass error prop here
                    />
                  </div>
                  <div>
                    <InputComponent
                      label="Speciality"
                      name="speciality"
                      type="text"
                      placeholder="Speciality"
                      value={values.speciality}
                      onChange={handleChange}
                      className=" w-full"
                      error={errors?.speciality} // Pass error prop here
                    />
                  </div>
                  <div>
                    <InputComponent
                      label="Qualification"
                      name="qualification"
                      type="text"
                      placeholder="Qualification"
                      value={values.qualification}
                      onChange={handleChange}
                      className=" w-full"
                      error={errors?.qualification} // Pass error prop here
                    />
                  </div>
                  <div>
                    <InputComponent
                      label="Consultation fee"
                      name="consultationFee"
                      type="text"
                      placeholder="Consultation fee"
                      value={values.consultationFee}
                      onChange={handleChange}
                      className=" w-full"
                      error={errors?.consultationFee} // Pass error prop here
                    />
                  </div>
                </div>
                {/* Working Days checkboxes */}
                <div className=" my-5">
                  <label className="block text-gray-700 text-sm mb-1 text-left">
                    Working Days
                  </label>

                  <div className="flex flex-wrap gap-2 justify-start">
                    {Object.keys(workingDays).map((day) => (
                      <label
                        className=" flex cursor-pointer items-center p-2 px-3 border border-gray-300 rounded-md
                      "
                        key={day}
                      >
                        <span className="relative inline-flex">
                          <input
                            type="checkbox"
                            checked={workingDays[day]}
                            onChange={() =>
                              setWorkingDays({
                                ...workingDays,
                                [day]: !workingDays[day],
                              })
                            }
                            className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border border-blue-gray-200 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-blue-500 checked:bg-blue-500 checked:before:bg-blue-500 hover:before:opacity-10"
                          />
                          <div className="pointer-events-none absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 text-white opacity-0 transition-opacity peer-checked:opacity-100">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3.5 w-3.5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              stroke="currentColor"
                              strokeWidth="1"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              ></path>
                            </svg>
                          </div>
                        </span>
                        <span className="ml-2 text-sm text-gray-700 capitalize">
                          {day}
                        </span>
                      </label>
                    ))}
                    {errors.working_days && (
                      <div className=" text-red-500 text-sm">
                        {errors.working_days}
                      </div>
                    )}
                  </div>
                </div>

                {/* Working Hours inputs */}
                <div className="flex flex-col gap-5">
                  {Object.keys(workingHours).map((day) => (
                    <div
                      key={day}
                      className={
                        workingDays[day]
                          ? "p-4 bg-blue-50 rounded-md"
                          : "hidden"
                      }
                    >
                      <div className="w-[40px] flex items-center border-b border-blue-500">
                        <label className="block text-gray-700 mb-1 text-left capitalize">
                          {day}
                        </label>
                      </div>

                      {Array.isArray(workingHours[day]) &&
                        workingHours[day].map((timeSlot, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 my-2"
                          >
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={timeSlot.enabled}
                                onChange={(e) =>
                                  handleWorkingHoursChange(
                                    day,
                                    index,
                                    "enabled",
                                    e.target.checked
                                  )
                                }
                                className="form-checkbox h-5 w-5 text-blue-500 transition duration-150 ease-in-out"
                              />
                              <span className="text-gray-700">
                                {index === 0 ? "Morning" : "Evening"}
                              </span>
                            </label>
                            <TimeSelect
                              value={timeSlot.start}
                              onChange={(value) =>
                                handleWorkingHoursChange(
                                  day,
                                  index,
                                  "start",
                                  value
                                )
                              }
                              disabled={!timeSlot.enabled}
                            />

                            <TimeSelect
                              value={timeSlot.end}
                              onChange={(value) =>
                                handleWorkingHoursChange(
                                  day,
                                  index,
                                  "end",
                                  value
                                )
                              }
                              disabled={!timeSlot.enabled}
                            />
                          </div>
                        ))}

                      {isFirstDayTimingsFilled() && (
                        <button
                          type="button"
                          onClick={() => copyTimingsToOtherDays(day)}
                          className="copy-timings-button"
                        >
                          Copy {day}'s timings to all selected days
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {!isEdit && (
                  <>
                    {!otpSent && (
                      <div className="mt-4 mb-4 text-left">
                        <p className="text-xs text-red-600">
                          *Click on Send OTP to verify Phone Number
                        </p>
                        <button
                          type="button"
                          onClick={async () => {
                            const isValidPhoneNumber =
                              await validatePhoneNumber(values.phoneNumber);
                            if (isValidPhoneNumber) {
                              const otpSentSuccess = await sendOtpToServer(
                                values.phoneNumber
                              );
                              if (otpSentSuccess) {
                                setOtpSent(true);
                                setShowVerifyUI(true);
                              } else {
                                setError(
                                  "Failed to send OTP. Please try again."
                                );
                              }
                            }
                          }}
                          className="bg-green-500 text-white rounded hover:bg-green-700 px-4 py-2 focus:outline-none"
                        >
                          Send OTP
                        </button>
                      </div>
                    )}

                    {showVerifyUI && (
                      <div className="my-4">
                        <InputComponent
                          type="tel"
                          placeholder="Enter OTP"
                          value={otp}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!isNaN(val)) {
                              setOtp(val);
                            }
                          }}
                          error={errors?.otp} // Pass error prop here
                        />
                      </div>
                    )}
                  </>
                )}

                {isEdit ? (
                  <div className="border-t border-gray-300 pt-6 pb-3 mt-8">
                    <button
                      type="submit"
                      className="w-full bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 flex justify-center items-center transition duration-200 ease-in-out"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <FaSpinner size={20} className="animate-spin mr-2" />
                      ) : (
                        "Update Doctor"
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-[1fr_1fr] border-t border-gray-300 gap-x-3 pt-6 pb-3 mt-8">
                    <button
                      type="submit"
                      className="w-full bg-blue-500 text-white py-3 rounded-md hover:bg-blue-600 flex justify-center items-center transition duration-200 ease-in-out"
                      disabled={isSubmitting || !otpSent}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center">
                          <FaSpinner size={20} className="animate-spin mr-2" />
                          <span>Submitting...</span>
                        </div>
                      ) : otpSent ? (
                        "Submit"
                      ) : (
                        "Add Doctor"
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        handleClear();
                        resetForm();
                      }}
                      className="px-4 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none transition duration-200 ease-in-out"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </form>
            )}
          </Formik>

          {error && <p className="text-red-500 text-center mt-4">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default AddDoctorModal;
